


import { useMemo, useState, useEffect } from 'react'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { CustomTable } from '../../../components/customTable'
import { PagePreloader } from '../../../components/pagePreloader'
import { RenderDefaultActions, RenderImageWizard, RenderStatus } from '../common'

import { usePopup } from '../../../context/popupContext'
import { useNotificationBar } from '../../../context/notificationContext'
import { twElementStyles } from '../../../twElementStyles'

import {
    getErrorResponse,
    getTimeRelativeTime,
    getUpdatedSearchParams,
    parseSearchParams,
} from '../../../services/util'

import { ListApiFilterParams } from 'interfaces/api-request'
import { SmoothTransition } from 'components/pageTransition'
import { useDeleteCollection, useGetCollections, useUpdateCollectionStatus, } from 'hooks/collection'
import ViewCollectionDialog from './ViewCollection'
import { renderWidgetChips } from 'components/label/RenderLabels'
import { ConflictDetailsDrawer } from 'components/ConflictDetailsDrawer'
import { useBulkLabelGenerationContext } from 'context/bulkLabelGenerationContext'
import { useActiveAIPrompt } from 'hooks/ai-settings'
import { SparklesIcon } from '@heroicons/react/24/outline'

const CollectionList = () => {
    // dialog
    const [addUpdate, setAddUpdate] = useState(false)
    const [editData, setEditData] = useState<any>(null)
    const [isEditForm, setIsEditForm] = useState(false)

    // popup/notification
    const popup = usePopup()
    const notification = useNotificationBar()
    let navigateTo = useNavigate()
    // selection
    const rowSelectionState = useState<RowSelectionState>({})

    // API hooks
    const { mutate: deleteShow } = useDeleteCollection()
    const { mutate: updateShowStatus } = useUpdateCollectionStatus()
    const { startGeneration, isGenerating, currentJob } = useBulkLabelGenerationContext()
    const { data: activePrompt } = useActiveAIPrompt()

    //Filter Params
    // let [searchParams, setSearchParams] = useSearchParams()
    let [searchParams, setSearchParams] = useSearchParams()
    let filterParams: ListApiFilterParams = parseSearchParams(searchParams, 'filter')

    // fetch data
    const { data, isLoading, isError, refetch, dataUpdatedAt } =
        useGetCollections(filterParams)

    //view dailog
    const [openView, setOpenView] = useState(false)
    const [viewData, setViewData] = useState(null)
    const [viewId, setViewId] = useState(null)

    const [deleteConflict, setDeleteConflict] = useState<{
        message: string
        details: string[]
    } | null>(null)

    const useFormHook = useForm({
        defaultValues: filterParams,
    })

    const responseData = useMemo(() => {
        if (!data?.data) return null

        return {
            results: Array.isArray(data.data.results) ? data.data.results : [],
            pages: data.data.pages ?? 1,
            total: data.data.total ?? 0,
        }
    }, [data])

    const handleBulkLabelGeneration = async () => {
        const selectedCollectionIds = Object.keys(rowSelectionState[0] || {})

        if (selectedCollectionIds.length === 0) {
            notification.push({
                title: 'Info',
                type: 'info',
                message: 'Please select at least one collection for bulk label generation.'
            })
            return
        }

        if (!activePrompt) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: 'No active AI prompt found. Please configure one in Settings.'
            })
            return
        }

        popup.open({
            title: 'Confirm Bulk Label Generation',
            message: (
                <>
                    <p className="mb-2">
                        You are about to generate labels for <strong>{selectedCollectionIds.length}</strong> collection(s).
                    </p>
                    <p className="mt-2">Are you sure you want to continue?</p>
                </>
            ),
            type: 'confirm',
            onAction: async (confirmed: boolean) => {
                if (!confirmed) return

                startGeneration(selectedCollectionIds, activePrompt.prompt, 'collection')
                rowSelectionState[1]({})
            },
            confirmButtonText: 'Generate Labels',
            confirmButtonClass: twElementStyles.twButtonElegant,
        })
    }

    useEffect(() => {
        if (currentJob?.status === 'completed' && currentJob?.entityType === 'collection') {
            refetch()
        }
    }, [currentJob, refetch])

    // -------------------------------
    //  OPEN EDIT / ADD DIALOG
    // -------------------------------
    const openAddUpdateDialog = (row: any, type: 'edit' | 'add') => {
        if (type === 'edit' && row) {
            setEditData({ ...row }) // clean object
            setIsEditForm(true)
        } else {
            setEditData(null)
            setIsEditForm(false)
        }
        setAddUpdate(true)
    }

    // -------------------------------
    //  STATUS TOGGLE (MATCH RESOURCE MODULE)
    // -------------------------------
    const onStatusChange = (row: any) => {
        const nextStatus = row.status ? 0 : 1

        popup.open({
            title: 'Are you sure?',
            message: `Do you want to ${nextStatus ? 'Activate' : 'Deactivate'
                } ${row.name} show?`,
            type: 'confirm',

            onAction: (value: boolean) => {
                if (!value) return
                const payload = { status: Number(nextStatus) };

                updateShowStatus(
                    {
                        id: row.id,
                        data: payload,
                    },
                    {
                        onSuccess: () => {
                            notification.push({
                                title: 'Success!',
                                message: `${row.name} status updated successfully!`,
                                type: 'success',
                            })
                            refetch()
                        },
                        onError: (error) => {
                            notification.push(getErrorResponse(error))
                        },
                    }
                )
            },
        })
    }

    // -------------------------------
    //  DELETE HANDLER (MATCH RESOURCE MODULE)
    // -------------------------------
    const onDeleteAction = (row: any) => {
        popup.open({
            title: 'Are you sure ?',
            message: (
                <>
                    Do you want to delete <strong>{row?.name}</strong> collection? This cannot be undone.
                </>
            ),
            type: 'confirm',
            onAction: (value: boolean) => {
                if (!value) return

                deleteShow(
                    {
                        id: row?.id,
                    },
                    {
                        onSuccess: () => {
                            notification.push({
                                title: `Collection deleted !`,
                                message: `Collection ${row?.name} has been deleted successfully.`,
                                type: 'info',
                            })
                            refetch()
                        },
                        onError: (error: any) => {
                            const response = error?.response?.data || {}
                            const statusCode = response?.statusCode
                            const message =
                                response?.message || 'Something went wrong'
                            const details = Array.isArray(response?.details)
                                ? response.details
                                : []

                            if (statusCode === 409) {
                                notification.push({
                                    title: 'Cannot Delete',
                                    message: (
                                        <div className="space-y-2">
                                            <p className="text-xs leading-snug text-slate-700 dark:text-slate-200">
                                                {message}
                                            </p>
                                            <button
                                                type="button"
                                                className="text-xs font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                onClick={() =>
                                                    setDeleteConflict({
                                                        message,
                                                        details,
                                                    })
                                                }
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    ),
                                    type: 'error',
                                })
                                return
                            }

                            notification.push({
                                title: 'Error',
                                message,
                                type: 'error',
                            })
                        },
                    }
                )
            },
            confirmButtonText: 'Delete',
            confirmButtonClass: twElementStyles.twButtonElegantRed,
        })
    }

    // -------------------------------
    //  GENERAL ACTION HANDLER
    // -------------------------------
    const onAction = (actionType: string, row: any) => {
        // Row already contains id, name, etc.
        if (actionType === 'view') {
            const fullRow = responseData?.results?.find(
                (item) => item.id === row.id
            )
            setViewData(fullRow || null)
            setOpenView(true)
        }
        if (actionType === 'edit') return navigateTo(`/collections/update/${row.id}`)
        if (actionType === 'delete') onDeleteAction(row)
    }

    // -------------------------------
    //  TABLE COLUMNS  (MATCH RESOURCE STYLE)
    // -------------------------------
    const columns: ColumnDef<any>[] = [
        {
            id: 'select',
            header: ({ table }) => {
                const isAllSelected = table.getIsAllRowsSelected()
                const isSomeSelected = table.getIsSomeRowsSelected()

                return (
                    <input
                        type="checkbox"
                        checked={isAllSelected}
                        ref={(input) => {
                            if (input) {
                                input.indeterminate = !isAllSelected && isSomeSelected
                            }
                        }}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                )
            },
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
            ),
            enableSorting: false,
        },
        {
            accessorKey: 'id',
            header: () => 'ID',
            footer: (info) => info.column.id,
            cell: ({ getValue }) => {
                const id = getValue() as string
                return (
                    <div className="font-mono text-xs">
                        {id}
                    </div>
                )
            },
            enableSorting: false,
        },

        {
            id: 'image',
            header: 'Thumbnail',
            accessorKey: 'thumbnail',
            cell: ({ row }) => RenderImageWizard({ row, accessorKey: 'thumbnail', className: 'w-20 h-12 object-cover rounded overflow-hidden' }),
            enableSorting: false
        },

        { accessorKey: 'name', header: 'NAME' },

        // {
        //     accessorKey: 'labels',
        //     header: 'LABELS',

        //     cell: ({ getValue }) => {
        //         const value = getValue()

        //         // Case 1: Already an array → join and show
        //         if (Array.isArray(value)) {
        //             return value.length ? renderWidgetChips(value) : '-'
        //         }

        //         // Case 2: String (possibly JSON string) → parse & show
        //         if (typeof value === 'string') {
        //             try {
        //                 const parsed = JSON.parse(value)
        //                 return Array.isArray(parsed) ? renderWidgetChips(parsed) : value
        //             } catch {
        //                 // simple string like "label1,label2"
        //                 return value
        //             }
        //         }

        //         return '-'
        //     },
        // },

        {
            accessorKey: 'entities',
            header: 'ENTITIES',
            enableSorting: false,
            cell: ({ row }) => {
                const entities = row.original.entities || []
                
                if (!entities.length) return '-'
                
                const entityNames = entities
                    .map((item: any) => {
                        // Handle different entity types: show, episode
                        return item.show?.name || item.episode?.name || item.name
                    })
                    .filter(Boolean)
                
                return entityNames.length ? renderWidgetChips(entityNames) : '-'
            }
        },

        // {
        //     accessorKey: 'shortDescription',
        //     header: 'SHORT SUMMARY',
        //     cell: ({ getValue }) => {
        //         const value = String(getValue() || "")
        //         return (
        //             <div className="w-[120px] truncate">
        //                 {value}
        //             </div>
        //         )
        //     },
        // },

        {
            accessorKey: 'createdAt',
            header: 'CREATED DATE',
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null

                return value ? moment(value).format('LL') : '-'
            },
        },

        {
            accessorKey: 'updatedAt',
            header: 'PUBLISH DATE',
            cell: ({ getValue }) => {
                const value = getValue() as string | number | Date | null

                return value ? moment(value).format('LL') : '-'
            },
        },

        {
            id: 'status',
            header: 'STATUS',
            accessorFn: (row) => Number(row.status),
            cell: ({ row }) => RenderStatus(row, 'status', onStatusChange),
        },

        {
            id: 'actions',
            header: 'ACTIONS',
            cell: ({ row }) =>
                RenderDefaultActions(row, ['edit', 'delete', 'view'], onAction),
        },
    ]

    const filterOptions = [
        {
            key: 'status',
            label: 'Status',
            options: [
                { display: 'All', value: '' },
                { display: 'Active', value: 1 },
                { display: 'Inactive', value: 0 },
            ],
        },
    ]

    useEffect(() => {
        const subscriber = useFormHook.watch((values, { name }) => {
            if (name) {
                let updated = getUpdatedSearchParams(
                    filterParams,
                    values,
                    name
                )
                if (Object.keys(updated)?.length) {
                    let sp = encodeURIComponent(JSON.stringify(updated))
                    setSearchParams({ filter: sp })
                } else {
                    setSearchParams({})
                }
            }
        })
        return () => subscriber.unsubscribe()
    }, [useFormHook, filterParams, setSearchParams])

    if (isLoading) return <PagePreloader />

    return (
        <SmoothTransition>
            <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg shadow">
                <ViewCollectionDialog
                    isOpen={openView}
                    setIsOpen={setOpenView}
                    collectionData={viewData}
                />

                <ConflictDetailsDrawer
                    isOpen={!!deleteConflict}
                    onClose={() => setDeleteConflict(null)}
                    title="Cannot Delete Collection"
                    description={deleteConflict?.message ?? ''}
                    details={deleteConflict?.details ?? []}
                />

                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            {Object.keys(rowSelectionState[0] || {}).length} selected
                        </span>
                    </div>
                    <button
                        onClick={handleBulkLabelGeneration}
                        disabled={Object.keys(rowSelectionState[0] || {}).length === 0 || isGenerating}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                        <SparklesIcon className="w-4 h-4" />
                        {isGenerating ? 'Generating...' : 'Generate Labels'}
                    </button>
                </div>

                <CustomTable
                    title="Collections"
                    titleLabel={
                        !isError
                            ? `Last fetched on ${getTimeRelativeTime(
                                dataUpdatedAt
                            )}`
                            : ''
                    }
                    columns={columns}
                    data={responseData?.results}
                    pages={responseData?.pages}
                    totalRecords={responseData?.total}
                    isLoading={isLoading}
                    formHook={useFormHook}
                    rowSelectionState={rowSelectionState}
                    useFieldForSelect="id"
                    filterOptions={filterOptions}
                    onRefresh={refetch}
                    onAddAction={() => navigateTo(`/collections/add`)}
                />
            </div>
        </SmoothTransition>
    )
}

export default CollectionList